/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useCallback, useMemo } from 'react';
import { Container, Typography, Button, Table, Badge, useToast } from '@crisellevaldez/speculo-ui';
import { SteppedModal } from '../../components/common/SteppedModal';
import { formatNumber } from '../../utils/format';
import { EditFlowModal } from '../../components/features/flows/EditFlowModal';
import { ViewFlowContactsModal } from '../../components/features/flows/ViewFlowContactsModal';
import { FlowActivityModal } from '../../components/features/flows/FlowActivityModal';
import {
  PlusCircle,
  Phone,
  UserPlus,
  Pencil,
  Trash,
  RotateCw,
  RotateCcw,
  Upload,
} from 'lucide-react';
import { ConfirmationDialog, LoadingDialog } from '../../components/common/dialogs';
import { Flow } from '../../hooks/useFlowsQuery';
import { useFlowsGQL } from '../../hooks/graphql/flows/useFlowsGQL';
import { OffsetPagination } from '../../components/common/OffsetPagination';
import { TableCard } from '../../components/common/TableCard';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { useHasIntegration } from '../../hooks/useHasIntegration';
import { useClientOwner } from '../../hooks/useClientOwner';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { useUpdateOrganizationSettings } from '../../hooks/organizations/useUpdateOrganizationSettings';
import { useUser } from '../../hooks/useUser';

import moment from 'moment-timezone';
import { getFlowStatusStyle } from '../../utils/badgeStyles';
import { capitalizeFirst } from '../../utils/strings';
import { ReviveFlowSteps } from '../../components/features/flows/ReviveFlowSteps';
import { createFlow } from '../../services/flows';
import { generateVoicemail as generateVoicemailAPI } from '../../api/flows';
import { deleteFlow } from '../../api/remiApi';
import { AddContactsModal } from '../../components/features/flows/AddContactsModal';
import { AddFlowContactsModal } from '../../components/features/flows/AddFlowContactsModal';
import { UploadDatabaseModal } from '../../components/features/flows/UploadDatabaseModal';
import { buildTransferLineOptions, formatTransferNumbers } from '../../utils/flows/formatTransferNumbers';
import { ReviveTableHeader } from '../../components/features/flows/ReviveTableHeader';
import { FlowDescriptionCard } from '../../components/features/flows/FlowDescriptionCard';
import { FlowActivityButton } from '../../components/features/flows/FlowActivityButton';
import { GuidedTour } from '../../components/common/GuidedTour/GuidedTour';
import { reviveTourSteps } from '../../components/features/flows/reviveTourSteps';
import { TourCompleteModal } from '../../components/features/flows/TourCompleteModal';
import { useOnboardingWebhook } from '../../components/features/onboarding/hooks/useOnboardingWebhook';
import { getScheduleWindowEndDate } from '../../utils/flows/scheduleUtils';

interface CreatedFlow {
  id: string;
  name: string;
  scheduling_window?: number;
  schedule_over_unit?: string;
  allowed_days: string[];
  allowed_hours?: {
    start_hour: number;
    end_hour: number;
  };
  exclude_holidays: boolean;
  useRoundRobin?: boolean;
  transfer_numbers: Record<string, string>;
  scheduled_for?: string;
  description?: string;
  flow_type: string;
}

// Valid flow statuses
const validStatuses = ['draft', 'scheduled', 'finished', 'active', 'paused'];

const ReviveFlowPage: React.FC = () => {
  const { showToast } = useToast();
  const { organization, fetchOrganization } = useOrganizationStore();
  const { users } = useOrganizationUsers();
  const [showAddContactsModal, setShowAddContactsModal] = useState(false);
  const [showTourCompleteModal, setShowTourCompleteModal] = useState(false);
  const [isSavingTourCompletion, setIsSavingTourCompletion] = useState(false);
  const [showViewContactsModal, setShowViewContactsModal] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');
  const [createdFlow, setCreatedFlow] = useState<CreatedFlow | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<Flow | null>(null);
  const [flowToEdit, setFlowToEdit] = useState<Flow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreatingFlow, setIsCreatingFlow] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewFlowModal, setShowNewFlowModal] = useState(false);
  const [newFlowStep, setNewFlowStep] = useState(0);
  // Track if a test contact is selected in EditFlowModal
  const [hasTestContactSelected, setHasTestContactSelected] = useState(false);
  const [selectedTestContactId, setSelectedTestContactId] = useState<string | null>(null);
  // Track if add test contact modal is open (for guided tour step navigation)
  const [isAddTestContactModalOpen, setIsAddTestContactModalOpen] = useState(false);
  // Track if tour was skipped (for showing different modal content)
  const [wasTourSkipped, setWasTourSkipped] = useState(false);
  // Controlled tour step - allows us to go back to previous steps
  const [controlledTourStep, setControlledTourStep] = useState(0);

  // Flow basic info
  const [flowName, setFlowName] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [transferLines, setTransferLines] = useState<string[]>([]);

  // Basic info errors
  const [flowNameError, setFlowNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  // Schedule settings
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [preferredTime, setPreferredTime] = useState<string | null>(null);
  const [schedulingValue, setSchedulingValue] = useState(365);
  const [schedulingUnit, setSchedulingUnit] = useState('days');
  const [allowedDays, setAllowedDays] = useState<string[]>([
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]);
  const [allowedStartTime, setAllowedStartTime] = useState<string | null>('09:00');
  const [allowedEndTime, setAllowedEndTime] = useState<string | null>('18:00');
  const [excludeHolidays, setExcludeHolidays] = useState(true);
  const [useRoundRobin, setUseRoundRobin] = useState(false);
  const [flowTimezone, setFlowTimezone] = useState<string>(organization?.timezone || 'UTC');
  const [flowCountry, setFlowCountry] = useState<string>('US');

  // Schedule errors
  const [startDateError, setStartDateError] = useState('');
  const [preferredTimeError, setPreferredTimeError] = useState('');
  const [schedulingError, setSchedulingError] = useState('');
  const [allowedDaysError, setAllowedDaysError] = useState('');

  const { updateSettings } = useUpdateOrganizationSettings();
  const { user, isAdmin, isSuperAdmin, loading: userLoading } = useUser();
  const { sendWebhook } = useOnboardingWebhook();

  // Check if Twilio is configured (phone number exists)
  const isTwilioConfigured = useMemo(() => {
    return Boolean(organization?.phoneNumbers?.inbound || organization?.phoneNumbers?.outbound);
  }, [organization?.phoneNumbers?.inbound, organization?.phoneNumbers?.outbound]);

  const hasIntegration = useHasIntegration();
  const clientOwner = useClientOwner();

  // Strategy call meeting link — follows the org's assigned client owner.
  const STRATEGY_CALL_LINK = clientOwner.strategyMeetingUrl;

  // Handler to save tour completion to Firebase and show success modal
  const handleTourComplete = useCallback(
    async (skipped: boolean = false) => {
      if (!organization?.id) return;
      setIsSavingTourCompletion(true);
      try {
        await updateSettings({
          id: organization.id,
          data: {
            onboardingSetup: {
              ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
              revive_tour_completed: true,
              revive_tour_completed_at: new Date().toISOString(),
              revive_tour_skipped: skipped,
            },
            // Auto-populate strategy call banner
            onboardingData: {
              ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
              crash_course: {
                meeting_link: STRATEGY_CALL_LINK,
                completed: false,
                auto_populated_at: new Date().toISOString(),
              },
            },
          } as any,
        });

        // Send webhook notification for onboarding complete (fire and forget)
        sendWebhook('Onboarding Complete', {
          action: skipped ? 'tour_skipped' : 'tour_completed',
          tourType: 'revive_flow',
        });

        // Refresh organization data to update the store
        await fetchOrganization(true);
        console.log('Revive tour completion saved to Firebase');
        // Show success modal
        setShowTourCompleteModal(true);
      } catch (error) {
        console.error('Error saving tour completion to Firebase:', error);
      } finally {
        setIsSavingTourCompletion(false);
      }
    },
    [organization?.id, updateSettings, fetchOrganization]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [sortConfig, setSortConfig] = useState<{ field?: string; direction: 'asc' | 'desc' }>({
    field: 'scheduledFor',
    direction: 'desc',
  });
  const [activeFilters, setActiveFilters] = useState<{
    status: string[];
    dateRange: { startDate: Date | null; endDate: Date | null };
  }>({
    status: [],
    dateRange: { startDate: null, endDate: null },
  });

  const {
    data,
    totalCount,
    currentPage,
    onNextPage,
    onPrevPage,
    onPageChange,
    isLoading: loading,
    error: queryError,
    refetch: originalRefetch,
  } = useFlowsGQL({
    flowType: 'Revive',
    organizationId: organization?.id || '',
    pageSize,
    sortField: sortConfig.field,
    sortDirection: sortConfig.direction,
    searchQuery,
    filters: {
      status: activeFilters.status,
      dateRange: activeFilters.dateRange,
    },
  });

  const flows = data?.pages[0]?.flows || [];

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSort = useCallback((field: string) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const error = queryError ? (queryError as Error).message : null;

  // Action Handlers
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedCallFlow, setSelectedCallFlow] = useState<Flow | null>(null);

  const handleViewActivity = useCallback((flow: Flow) => {
    if (!validStatuses.includes(flow.status.toLowerCase())) return;
    setSelectedCallFlow(flow);
    setShowActivityModal(true);
  }, []);

  const handleViewContacts = useCallback((flow: Flow) => {
    if (!validStatuses.includes(flow.status.toLowerCase())) return;
    setSelectedFlowId(flow.id);
    setShowViewContactsModal(true);
  }, []);

  const [showAddFlowContactsModal, setShowAddFlowContactsModal] = useState(false);
  const [selectedAddFlow, setSelectedAddFlow] = useState<Flow | null>(null);
  const [showUploadDatabaseModal, setShowUploadDatabaseModal] = useState(false);

  const handleAddContacts = useCallback((flow: Flow) => {
    if (!validStatuses.includes(flow.status.toLowerCase())) return;
    setSelectedAddFlow(flow);
    setShowAddFlowContactsModal(true);
  }, []);

  const handleDeleteClick = useCallback((flow: Flow) => {
    if (!validStatuses.includes(flow.status.toLowerCase())) return;
    setFlowToDelete(flow);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!flowToDelete) return;

    setIsDeleting(true);
    try {
      await deleteFlow(organization?.id || '', flowToDelete.id);
      await originalRefetch();
      showToast({
        message: 'Flow archived successfully',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error archiving flow:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to archive flow',
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setFlowToDelete(null);
    }
  }, [flowToDelete, originalRefetch, showToast]);

  return (
    <Container className="px-4 lg:px-5 xl:pl-20 xl:pr-5 pb-6">
      {/* First-time user guided tour - only show if reviveTourCompleted is explicitly false */}
      <GuidedTour
        steps={reviveTourSteps}
        storageKey="revive-flow-tour-completed"
        forceShow={(() => {
          const setup = (organization as any)?.onboardingSetup;
          const tourCompleted =
            setup?.reviveTourCompleted ?? setup?.revive_tour_completed;
          return !userLoading && !isSuperAdmin && tourCompleted === false;
        })()}
        controlledStep={controlledTourStep}
        onRequestStepChange={async (fromStep, toStep) => {
          // Handle step change requests - update the controlled step
          // Special case: Skip step 18 when advancing normally from step 17
          // Step 18 is only shown when user explicitly clicks add button
          let actualToStep = toStep;
          if (fromStep === 17 && toStep === 18 && !isAddTestContactModalOpen) {
            // Skip to step 19 (test call button)
            actualToStep = 19;
          }

          // Skip test call steps (17-19) if Twilio is not configured
          // Complete the tour after step 16 (voicemail) when Twilio is not setup
          if (fromStep === 16 && toStep === 17) {
            // Refresh organization data to get latest Twilio status
            // This handles the case where Twilio was set up during the tour
            await fetchOrganization(true);

            // Re-check Twilio status with fresh data from store (not closure)
            // We use getState() to get the freshest data after fetchOrganization
            const freshOrg = useOrganizationStore.getState().organization;
            const latestTwilioConfigured = Boolean(
              freshOrg?.phoneNumbers?.inbound || freshOrg?.phoneNumbers?.outbound
            );

            if (!latestTwilioConfigured) {
              // Twilio not configured - complete the tour early
              // Set controlledTourStep beyond valid range to immediately hide the tour
              // This prevents the tour from showing while Firebase updates
              setControlledTourStep(reviveTourSteps.length);
              setWasTourSkipped(false);
              handleTourComplete();
              return; // Don't update step - tour is completing
            }
          }

          setControlledTourStep(actualToStep);

          // Handle side effects for step changes
          // Close AddContactsModal and open EditFlowModal when advancing from step 14 to step 15
          if (fromStep === 14 && actualToStep === 15) {
            setShowAddContactsModal(false);
            // Auto-open EditFlowModal with the newly created flow
            if (createdFlow?.id) {
              const flowFromList = flows.find((f) => f.id === createdFlow.id);
              if (flowFromList) {
                setFlowToEdit(flowFromList);
                setShowEditModal(true);
              }
            }
          }
          // Clear createdFlow after final step (step 19)
          if (fromStep === 19) {
            setCreatedFlow(null);
          }
        }}
        onComplete={() => {
          setWasTourSkipped(false);
          handleTourComplete();
        }}
        onSkip={() => {
          setWasTourSkipped(true);
          handleTourComplete();
        }}
        onStepChange={(fromStep, toStep) => {
          // Note: In controlled mode, step updates are handled by onRequestStepChange
          // This callback is only for side effects like opening/closing modals

          // Close AddContactsModal and open EditFlowModal when advancing from step 14 to step 15
          if (fromStep === 14 && toStep === 15) {
            setShowAddContactsModal(false);
            // Auto-open EditFlowModal with the newly created flow
            if (createdFlow?.id) {
              const flowFromList = flows.find((f) => f.id === createdFlow.id);
              if (flowFromList) {
                setFlowToEdit(flowFromList);
                setShowEditModal(true);
              }
            }
          }
          // Clear createdFlow after final step (now step 19)
          if (fromStep === 19) {
            setCreatedFlow(null);
          }
        }}
        canAdvance={(stepIndex) => {
          // Full tour: 20 steps (0-19)
          // Pattern: Input step -> Next button step -> Input step -> Next button step...
          // With AddTeamMemberModal flow, Loading dialog, and optional AddTestContactModal
          switch (stepIndex) {
            case 0: // Step 0: Click New Flow button (waitForClick handles this)
              return showNewFlowModal;
            case 1: // Step 1: Enter flow name - require flow name to be entered
              return showNewFlowModal && flowName.trim().length > 0;
            case 2: // Step 2: Click Next after name (waitForClick) - require on wizard step 1
              return showNewFlowModal && newFlowStep >= 1;
            case 3: // Step 3: Enter description - require description to be entered
              return showNewFlowModal && customDescription.trim().length > 0;
            case 4: // Step 4: Click Next after description (waitForClick) - require on wizard step 2
              return showNewFlowModal && newFlowStep >= 2;
            case 5: // Step 5: Click Add New Team button (waitForClick)
              return true; // AddTeamMemberModal will open
            case 6: // Step 6: Fill in team member form and submit - require team member added
              return transferLines.length > 0;
            case 7: // Step 7: Click Next after transfer line (waitForClick) - require on wizard step 3
              return showNewFlowModal && newFlowStep >= 3;
            case 8: // Step 8: Schedule date/time - require date and time to be set
              return showNewFlowModal && startDate !== null && preferredTime !== null;
            case 9: // Step 9: Advanced Settings - always allow (informational step)
              return true;
            case 10: // Step 10: Click Next after schedule (waitForClick) - require on wizard step 4
              return showNewFlowModal && newFlowStep >= 4;
            case 11: // Step 11: Summary review - always allow
              return showNewFlowModal;
            case 12: // Step 12: Click Complete (waitForClick) - require flow creation started
              return isCreatingFlow;
            case 13: // Step 13: Flow building loading - advance when AddContactsModal opens
              return showAddContactsModal;
            case 14: // Step 14: Add contacts modal - skip adding contacts (always allow Next)
              return true;
            case 15: // Step 15: Edit description - always allow
              return showEditModal;
            case 16: // Step 16: Edit voicemail - always allow
              return showEditModal;
            case 17: // Step 17: Test call section - require EditFlowModal open AND test contact selected
              // Don't allow advancing if add test contact modal is open
              if (isAddTestContactModalOpen) {
                return false;
              }
              return showEditModal && hasTestContactSelected;
            case 18: // Step 18: Add test contact form - don't allow Next when form is open
              // User must submit (form closes) and then select the contact from dropdown
              // After form closes, we go back to step 17 where they select from dropdown
              return false; // Never allow Next from step 18 - user must go back to step 17
            case 19: // Step 19: Test call button - always allow (final step)
              return true;
            default:
              return true;
          }
        }}
      />

      <div className="flex flex-col space-y-6 lg:space-y-10">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:gap-6">
          {/* Title and Actions */}
          <div>
            <div className="flex flex-col lg:flex-row justify-between lg:justify-start items-start lg:items-center gap-3 lg:gap-20">
              <div>
                <Typography variant="h3" as="h1" className="!text-xl font-bold">
                  Revive Flows
                </Typography>
                <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                  {formatNumber(totalCount)} {totalCount === 1 ? 'record' : 'records'}
                </Typography>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
                <Button
                  variant="primary"
                  size="md"
                  className="flex items-center justify-center gap-2"
                  onClick={() => setShowNewFlowModal(true)}
                  data-tour="revive-new-flow-button"
                  disabled={!isAdmin}
                >
                  <PlusCircle size={16} />
                  New Revive Flow
                </Button>
                {!hasIntegration && (
                  <Button
                    variant="outline"
                    size="md"
                    className="flex items-center justify-center gap-2"
                    onClick={() => setShowUploadDatabaseModal(true)}
                    disabled={!isAdmin}
                  >
                    <Upload size={16} />
                    Upload Database
                  </Button>
                )}
                {!isAdmin && (
                  <p className="text-xs text-muted-foreground self-center">
                    For launching new flows, contact the admin on your account.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <FlowDescriptionCard
            flowType="revive"
            description={[
              'Revive Flows are the other entry point to the Follow-Up Flywheel—they pull your old, dormant leads back in so they never get neglected again.',
              "Remi re-engages them with real context (what they asked about, homes they liked, values they tracked), then keeps nurturing them automatically and personally until they're ready. Convert is for new leads to enter the flywheel instantly; Revive is for past leads to re-enter it for good—either way, nobody falls out of your world.",
            ]}
            icon={RotateCcw}
            dataTour="revive-description"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2" data-tour="revive-search">
          <ReviveTableHeader
            searchQuery={searchQuery}
            onSearchChange={handleSearch}
            onRefresh={async () => {
              try {
                // Reset search and filters
                setSearchQuery('');
                setActiveFilters({
                  status: [],
                  dateRange: { startDate: null, endDate: null },
                });
                await originalRefetch();
              } catch (error) {
                console.error('Error refreshing flows:', error);
                showToast({
                  message: 'Failed to refresh flows',
                  variant: 'error',
                });
              }
            }}
            onFilter={setActiveFilters}
            isLoading={loading}
            totalFlows={totalCount}
          />

          <TableCard
            className="h-[60vh] sm:h-[calc(100vh-220px)]"
            data-tour="revive-table"
            footer={
              <OffsetPagination
                currentPage={currentPage}
                totalItems={totalCount}
                pageSize={pageSize}
                onNextPage={onNextPage}
                onPrevPage={onPrevPage}
                onPageChange={onPageChange}
                onPageSizeChange={(size) => { setPageSize(size); }}
                isLoading={loading}
                label="flows"
              />
            }
          >
            <Table<Flow>
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  width: '300px',
                  resizable: true,
                  sortable: true,
                  isPinned: true,
                  pinPosition: 'left',
                  cell: (row) => {
                    const isValidStatus = validStatuses.includes(row.status.toLowerCase());
                    const isCreatedFlow = createdFlow?.id === row.id;
                    return (
                      <div
                        className={`${!isValidStatus ? 'opacity-50' : ''}`}
                        {...(isCreatedFlow ? { 'data-tour': 'created-flow-row' } : {})}
                      >
                        {row.name}
                      </div>
                    );
                  },
                },
                {
                  key: 'status',
                  header: 'Status',
                  width: '100px',
                  sortable: true,
                  cell: (row) => {
                    const status = capitalizeFirst(row.status);
                    const style = getFlowStatusStyle(status);
                    const isValidStatus = validStatuses.includes(row.status.toLowerCase());
                    return (
                      <div className={`${!isValidStatus ? 'opacity-50' : ''}`}>
                        <Badge className={`${style.background} ${style.text} font-medium`}>
                          {status}
                        </Badge>
                      </div>
                    );
                  },
                },
                {
                  key: 'scheduled_for',
                  header: 'Scheduled for',
                  width: '230px',
                  sortable: true,
                  cell: (row) => {
                    const isValidStatus = validStatuses.includes(row.status.toLowerCase());
                    return (
                      <div className={`whitespace-nowrap ${!isValidStatus ? 'opacity-50' : ''}`}>
                        {row.scheduled_for}
                      </div>
                    );
                  },
                },
                {
                  key: 'schedule_window_end',
                  header: 'Schedule Window Ends',
                  width: '230px',
                  sortable: false,
                  cell: (row) => {
                    const isValidStatus = validStatuses.includes(row.status.toLowerCase());
                    const scheduleWindowEnd = getScheduleWindowEndDate(
                      row.scheduled_for_raw as string,
                      row.scheduling_window as number,
                      row.schedule_over_unit as string,
                      organization?.timezone || 'UTC'
                    );
                    return (
                      <div className={`whitespace-nowrap ${!isValidStatus ? 'opacity-50' : ''}`}>
                        {scheduleWindowEnd}
                      </div>
                    );
                  },
                },
                {
                  key: 'contacts',
                  header: 'Contacts',
                  width: '120px',
                  isCentered: true,
                  sortable: false,
                  cell: (row) => {
                    if (!row.contacts_count) return <span className="text-muted-foreground">-</span>;
                    const isValidStatus = validStatuses.includes(row.status.toLowerCase());
                    return (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewContacts(row)}
                        className={`px-3 py-1 text-xs ${
                          !isValidStatus ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={!isValidStatus}
                      >
                        {formatNumber(row.contacts_count ?? 0)}
                      </Button>
                    );
                  },
                },
                {
                  key: 'activity',
                  header: 'Activity',
                  width: '120px',
                  isCentered: true,
                  sortable: false,
                  cell: (row) => {
                    const isValidStatus = validStatuses.includes(row.status.toLowerCase());
                    return (
                      <FlowActivityButton
                        calls={row.calls_count}
                        sms={row.sms_count}
                        email={row.email_count}
                        onClick={() => handleViewActivity(row)}
                        className={!isValidStatus ? 'opacity-50 cursor-not-allowed' : ''}
                        disabled={!isValidStatus}
                      />
                    );
                  },
                },
                {
                  key: 'actions',
                  header: <span data-tour="actions-column-header">Actions</span>,
                  isCentered: true,
                  width: '160px',
                  sortable: false,
                  isPinned: true,
                  pinPosition: 'right',
                  cell: (row) => {
                    const isValidStatus = validStatuses.includes(row.status.toLowerCase());
                    const isCreatedFlow = createdFlow?.id === row.id;

                    return (
                      <div
                        className="relative flex items-center justify-center gap-2"
                        {...(isCreatedFlow ? { 'data-tour': 'created-flow-actions' } : {})}
                      >
                        {/* Add Contacts */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddContacts(row)}
                          className={`p-2 ${
                            isValidStatus ? 'hover:bg-gray-100 dark:hover:bg-white/10' : 'opacity-50 cursor-not-allowed'
                          }`}
                          title="Add Contacts"
                          disabled={!isValidStatus || row.status.toLowerCase() === 'finished'}
                        >
                          <UserPlus
                            className={`w-3.5 h-3.5 ${
                              isValidStatus ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400'
                            }`}
                          />
                        </Button>

                        {/* Edit */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (!isValidStatus) return;
                            setFlowToEdit(row);
                            setShowEditModal(true);
                          }}
                          className={`p-2 ${
                            isValidStatus ? 'hover:bg-gray-100 dark:hover:bg-white/10' : 'opacity-50 cursor-not-allowed'
                          }`}
                          title="Edit"
                          disabled={!isValidStatus}
                        >
                          <Pencil
                            className={`w-3.5 h-3.5 ${
                              isValidStatus ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400'
                            }`}
                          />
                        </Button>

                        {/* Delete — visible for everyone, disabled for agents */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteClick(row)}
                          className={`p-2 ${
                            isValidStatus && isAdmin
                              ? 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 border-red-300 dark:border-red-700/30 hover:border-red-500'
                              : 'opacity-50 cursor-not-allowed'
                          }`}
                          title={!isAdmin ? 'Only your organization admin can delete flows' : 'Delete'}
                          disabled={
                            !isAdmin ||
                            !isValidStatus ||
                            isDeleting ||
                            !['draft', 'paused'].includes(row.status.toLowerCase())
                          }
                        >
                          <Trash
                            className={`w-3.5 h-3.5 ${isValidStatus && isAdmin ? '' : 'text-gray-400'}`}
                          />
                        </Button>

                        {/* Loading Animation for Invalid Status */}
                        {!isValidStatus && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50">
                            <RotateCw className="w-4 h-4 animate-spin text-primary" />
                          </div>
                        )}
                      </div>
                    );
                  },
                },
              ]}
              data={flows}
              keyExtractor={(item) => item.id}
              sortable={true}
              onSort={handleSort}
              loading={loading}
              emptyStateText="No revive flows available"
            />
          </TableCard>

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={showDeleteModal}
            onClose={() => {
              if (!isDeleting) {
                setShowDeleteModal(false);
                setFlowToDelete(null);
              }
            }}
            onConfirm={handleConfirmDelete}
            title="Delete Flow"
            message={
              flowToDelete
                ? `Are you sure you want to delete the flow "${flowToDelete.name}"?`
                : 'Are you sure you want to delete this flow?'
            }
            confirmLabel="Delete"
            isLoading={isDeleting}
            variant="danger"
          />

          {/* Edit Flow Modal */}
          <FlowActivityModal
            isOpen={showActivityModal}
            onClose={() => {
              setShowActivityModal(false);
              setSelectedCallFlow(null);
            }}
            flowId={selectedCallFlow?.id || ''}
            flowName={selectedCallFlow?.name || ''}
            calls={selectedCallFlow?.calls_count}
            sms={selectedCallFlow?.sms_count}
            email={selectedCallFlow?.email_count}
          />

          <EditFlowModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setFlowToEdit(null);
              setHasTestContactSelected(false);
              setIsAddTestContactModalOpen(false);
            }}
            flow={flowToEdit}
            onSuccess={async () => {
              await originalRefetch();
            }}
            onTestContactChange={(contactId) => {
              setHasTestContactSelected(!!contactId);
              setSelectedTestContactId(contactId);
            }}
            onAddTestContactModalChange={(isOpen) => {
              setIsAddTestContactModalOpen(isOpen);
              // Handle guided tour step navigation
              if (isOpen && controlledTourStep === 17) {
                // Opening add test contact form - go to step 18
                setControlledTourStep(18);
              } else if (!isOpen && controlledTourStep === 18) {
                // Closing add test contact form - go back to step 17
                setControlledTourStep(17);
              }
            }}
          />

          {/* View Contacts Modal */}
          <ViewFlowContactsModal
            isOpen={showViewContactsModal}
            onClose={() => {
              setShowViewContactsModal(false);
              setSelectedFlowId('');
            }}
            flowId={selectedFlowId}
          />

          {/* New Flow Stepped Modal */}
          <SteppedModal
            isOpen={showNewFlowModal}
            onClose={() => {
              setShowNewFlowModal(false);
              // Only reset errors and step, keep the values
              setFlowNameError('');
              setDescriptionError('');
              setNewFlowStep(0);
            }}
            headerTitle="New Revive Flow"
            title={
              newFlowStep === 0
                ? 'What is the name of this Revive flow?'
                : newFlowStep === 1
                  ? 'Describe your Revive flow'
                  : newFlowStep === 2
                    ? 'Select transfer line'
                    : newFlowStep === 3
                      ? 'Schedule your Revive flow'
                      : 'Revive flow summary'
            }
            currentStep={newFlowStep}
            onNext={() => setNewFlowStep((prev) => prev + 1)}
            disableNext={
              (newFlowStep === 0 && !flowName.trim()) ||
              (newFlowStep === 1 && !customDescription.trim()) ||
              (newFlowStep === 2 && (!transferLines || transferLines.length === 0)) ||
              (newFlowStep === 3 && (!startDate || !preferredTime))
            }
            onPrevious={() => setNewFlowStep((prev) => prev - 1)}
            onReset={() => {
              // Reset basic info
              setFlowName('');
              setSelectedDescription('');
              setCustomDescription('');
              setTransferLines([]);

              // Reset schedule settings
              setStartDate(null);
              setPreferredTime(null);
              setFlowTimezone(organization?.timezone || 'UTC');
              setFlowCountry('US');

              // Reset all errors
              setFlowNameError('');
              setDescriptionError('');
              setStartDateError('');
              setPreferredTimeError('');

              // Reset step
              setNewFlowStep(0);
            }}
            onComplete={async () => {
              setIsCreatingFlow(true);
              try {
                // Get selected transfer line options and format them
                const selectedTransferOptions = buildTransferLineOptions(users).filter((opt) =>
                  transferLines.includes(opt.value)
                );
                const formattedNumbers = formatTransferNumbers(selectedTransferOptions);

                // Get first transfer number for call_settings
                const transferPhoneNumber = selectedTransferOptions[0]?.value || null;

                // Generate voicemail content first so we can include it in
                // the createFlow payload — avoids a follow-up PUT that would
                // null out other fields.
                let voicemailContent: string | undefined;
                try {
                  const vmResponse = await generateVoicemailAPI(customDescription);
                  voicemailContent = vmResponse.voicemail;
                } catch (vmError) {
                  console.warn('Voicemail generation failed:', vmError);
                }

                // Create flow and get response
                const response = await createFlow({
                  name: flowName,
                  description: customDescription,
                  description_id: selectedDescription,
                  flow_type: 'Revive',
                  sub_type: '', // Empty string for Revive flows
                  timezone: flowTimezone,
                  country_code: flowCountry,
                  scheduled_for:
                    startDate && preferredTime
                      ? moment
                          .tz(
                            `${moment(startDate).format('YYYY-MM-DD')} ${preferredTime}`,
                            'YYYY-MM-DD HH:mm',
                            flowTimezone
                          )
                          .format()
                      : '',
                  scheduling_window: schedulingValue,
                  schedule_over_unit: schedulingUnit,
                  allowed_days: allowedDays,
                  allowed_hours: {
                    start_hour: parseInt(allowedStartTime?.split(':')[0] || '9'),
                    end_hour: parseInt(allowedEndTime?.split(':')[0] || '18'),
                  },
                  exclude_holidays: excludeHolidays,
                  useRoundRobin: useRoundRobin,
                  transfer_numbers: formattedNumbers,
                  voicemail_content: voicemailContent,
                });

                // Refresh flows list and show success
                await originalRefetch();
                showToast({
                  message: 'Flow created successfully',
                  variant: 'success',
                });

                // Store flow details and show add contacts modal
                setCreatedFlow({
                  id: response.flowId,
                  name: flowName,
                  scheduling_window: schedulingValue,
                  schedule_over_unit: schedulingUnit,
                  allowed_days: allowedDays,
                  allowed_hours: {
                    start_hour: parseInt(allowedStartTime?.split(':')[0] || '9'),
                    end_hour: parseInt(allowedEndTime?.split(':')[0] || '18'),
                  },
                  exclude_holidays: excludeHolidays,
                  useRoundRobin: useRoundRobin,
                  transfer_numbers: formattedNumbers,
                  scheduled_for:
                    startDate && preferredTime
                      ? moment
                          .tz(
                            `${moment(startDate).format('YYYY-MM-DD')} ${preferredTime}`,
                            'YYYY-MM-DD HH:mm',
                            organization?.timezone || 'UTC'
                          )
                          .format()
                      : undefined,
                  description: customDescription,
                  flow_type: 'Revive',
                });

                setShowAddContactsModal(true);

                // Reset all form values and close modal
                setFlowName('');
                setSelectedDescription('');
                setCustomDescription('');
                setTransferLines([]);
                setStartDate(null);
                setPreferredTime(null);
                setSchedulingValue(365);
                setSchedulingUnit('days');
                setAllowedDays([
                  'Monday',
                  'Tuesday',
                  'Wednesday',
                  'Thursday',
                  'Friday',
                  'Saturday',
                ]);
                setAllowedStartTime('09:00');
                setAllowedEndTime('18:00');
                setExcludeHolidays(true);
                setUseRoundRobin(false);
                setFlowTimezone(organization?.timezone || 'UTC');
                setFlowCountry('US');
                setNewFlowStep(0);

                setShowNewFlowModal(false);
              } catch (error) {
                console.error('Error creating flow:', error);
                showToast({
                  message: error instanceof Error ? error.message : 'Failed to create flow',
                  variant: 'error',
                });
              } finally {
                setIsCreatingFlow(false);
              }
            }}
            size="xl"
          >
            {[0, 1, 2, 3, 4].map((stepIndex) => (
              <div key={stepIndex}>
                <ReviveFlowSteps
                  step={stepIndex}
                  // Flow basic info
                  flowName={flowName}
                  selectedDescription={selectedDescription}
                  customDescription={customDescription}
                  transferLines={transferLines}
                  // Schedule settings
                  startDate={startDate}
                  preferredTime={preferredTime}
                  schedulingValue={schedulingValue}
                  schedulingUnit={schedulingUnit}
                  allowedDays={allowedDays}
                  allowedStartTime={allowedStartTime}
                  allowedEndTime={allowedEndTime}
                  excludeHolidays={excludeHolidays}
                  // Errors
                  flowNameError={flowNameError}
                  descriptionError={descriptionError}
                  startDateError={startDateError}
                  preferredTimeError={preferredTimeError}
                  schedulingError={schedulingError}
                  allowedDaysError={allowedDaysError}
                  // Basic info handlers
                  onFlowNameChange={(value) => {
                    setFlowName(value);
                    if (flowNameError) setFlowNameError('');
                  }}
                  onSelectedDescriptionChange={setSelectedDescription}
                  onCustomDescriptionChange={(value) => {
                    setCustomDescription(value);
                    if (descriptionError) setDescriptionError('');
                  }}
                  onTransferLinesChange={setTransferLines}
                  // Schedule handlers
                  onStartDateChange={setStartDate}
                  onPreferredTimeChange={setPreferredTime}
                  onSchedulingValueChange={(value) => {
                    setSchedulingValue(value);
                    if (schedulingError) setSchedulingError('');
                  }}
                  onSchedulingUnitChange={setSchedulingUnit}
                  onAllowedDaysChange={setAllowedDays}
                  onAllowedStartTimeChange={setAllowedStartTime}
                  onAllowedEndTimeChange={setAllowedEndTime}
                  onExcludeHolidaysChange={setExcludeHolidays}
                  useRoundRobin={useRoundRobin}
                  onUseRoundRobinChange={setUseRoundRobin}
                  timezone={flowTimezone}
                  country={flowCountry}
                  onTimezoneChange={setFlowTimezone}
                  onCountryChange={setFlowCountry}
                  onInviteModalChange={(isOpen) => {
                    if (!isOpen && controlledTourStep === 6) {
                      setControlledTourStep(7);
                    }
                  }}
                />
              </div>
            ))}
          </SteppedModal>

          {/* Loading Dialog - Flow Creation */}
          <LoadingDialog
            isOpen={isCreatingFlow}
            message="Flow is building..."
            data-tour="flow-building-dialog"
          />

          {/* Loading Dialog - Tour Completion */}
          <LoadingDialog isOpen={isSavingTourCompletion} message="Completing onboarding..." />

          {/* Add Flow Contacts Modal */}
          <AddFlowContactsModal
            isOpen={showAddFlowContactsModal}
            onClose={() => {
              setShowAddFlowContactsModal(false);
              setSelectedAddFlow(null);
            }}
            flowId={selectedAddFlow?.id || ''}
            flowName={selectedAddFlow?.name || ''}
            onSuccess={async () => {
              await originalRefetch();
            }}
          />

          {/* Add Contacts Modal */}
          <AddContactsModal
            isOpen={showAddContactsModal}
            onClose={async () => {
              setShowAddContactsModal(false);
              const newFlowId = createdFlow?.id;
              setCreatedFlow(null);
              if (!newFlowId) return;
              // Refetch to pick up contact count and status change
              // (draft → scheduled) from ScheduleFlowModal. Use the
              // returned flows directly to avoid stale closure data.
              const freshFlows = await originalRefetch();
              const flowFromList = (freshFlows as Flow[]).find((f) => f.id === newFlowId);
              if (flowFromList) {
                setFlowToEdit(flowFromList);
                setShowEditModal(true);
              }
            }}
            flowName={createdFlow?.name ?? ''}
            flowId={createdFlow?.id ?? ''}
            schedulingWindow={createdFlow?.scheduling_window}
            scheduleOverUnit={createdFlow?.schedule_over_unit}
            allowedDays={createdFlow?.allowed_days ?? []}
            allowedHours={createdFlow?.allowed_hours}
            excludeHolidays={createdFlow?.exclude_holidays ?? false}
            transferNumbers={createdFlow?.transfer_numbers ?? {}}
            timezone={organization?.timezone}
            scheduledFor={createdFlow?.scheduled_for}
            description={createdFlow?.description}
          />

          {/* Tour Complete Success Modal */}
          <TourCompleteModal
            isOpen={showTourCompleteModal}
            onClose={() => setShowTourCompleteModal(false)}
            contactId={selectedTestContactId}
            wasSkipped={wasTourSkipped}
          />

          {/* Upload Database Modal */}
          <UploadDatabaseModal
            isOpen={showUploadDatabaseModal}
            onClose={() => setShowUploadDatabaseModal(false)}
          />
        </div>
      </div>
    </Container>
  );
};

export default ReviveFlowPage;
